import type { QueryClient } from "@tanstack/solid-query";
import { BankAccountResource } from "~/features/bank-account/bank-account.resource";
import { BankTransactionResource } from "~/features/bank-transaction/bank-transaction.resource";
import { DocumentsResource } from "~/features/document/document.resource";
import { FixedAssetsResource } from "~/features/fixed-assets/fixed-assets.resource";
import { JournalEntriesResource } from "~/features/journal-entry/journal-entry.resource";
import { VendorsResource } from "~/features/vendor/vendor.resource";
import { WorksheetsResource } from "~/features/worksheets/worksheets.resource";
import { AssociationsResource } from "./association.resource";
import { EntityTypeValues, type EntityType } from "./association.schema";

/**
 * Prefetch all queries the Associations panel needs for a given entity.
 *
 * The Associations panel (used on every detail route) fires several queries on
 * mount: the association list itself plus the tab-list resource for each tab
 * (documents, journal entries, vendors, worksheets, fixed assets). Without
 * prefetching, these fire fresh on first navigation → the panel flashes loading
 * states.
 *
 * Call this from a detail route's `loader` (which runs during hover preload) so
 * the data is cached before the user navigates. It is fire-and-forget (NOT
 * awaited) — it must never block the loader's critical path or SSR.
 *
 * The helper prefetches the tab-list resource for every entity type EXCEPT the
 * one we're currently on (an entity can't associate with itself, so the panel
 * never shows a tab for the current type). This keeps it route-aware without
 * needing to pass the panel's tab config — e.g. on the journal-entry route it
 * prefetches the documents list, not journal entries.
 *
 * @param qc         The router's QueryClient (from loader `context.queryClient`).
 * @param businessId Active business id.
 * @param entityType The source entity type (e.g. "document", "vendor").
 * @param entityId   The source entity id.
 */
export function prefetchAssociations(
  qc: QueryClient,
  businessId: string,
  entityType: EntityType,
  entityId: string,
) {
  void qc.prefetchQuery(
    AssociationsResource.options({ businessId, entityType, entityId }),
  );
  // Prefetch the tab-list resource for every entity type except the current
  // one (the panel never shows a tab for the entity we're on).
  for (const type of EntityTypeValues) {
    if (type === entityType) continue;
    prefetchTabList(qc, businessId, type);
  }
}

function prefetchTabList(
  qc: QueryClient,
  businessId: string,
  targetType: EntityType,
) {
  switch (targetType) {
    case "journal-entry":
      void qc.prefetchQuery(
        JournalEntriesResource.options({
          businessId,
          limit: 20,
          sortBy: "date",
          sortDir: "desc",
        }),
      );
      break;
    case "document":
      void qc.prefetchQuery(
        DocumentsResource.options({ businessId, limit: 20 }),
      );
      break;
    case "vendor":
      void qc.prefetchQuery(VendorsResource.options({ businessId, limit: 20 }));
      break;
    case "worksheet":
      void qc.prefetchQuery(
        WorksheetsResource.options({ businessId, limit: 20 }),
      );
      break;
    case "fixed-asset":
      void qc.prefetchQuery(
        FixedAssetsResource.options({ businessId, limit: 20 }),
      );
      break;
    case "bank-transaction":
      void qc.prefetchQuery(
        BankTransactionResource.options({
          businessId,
          limit: 20,
          sortBy: "date",
          sortDir: "desc",
        }),
      );
      break;
    case "bank-account":
      void qc.prefetchQuery(
        BankAccountResource.options({ businessId, limit: 20 }),
      );
      break;
    default:
      break;
  }
}
